// @ts-nocheck
import { useState } from 'react';
import { Check, CaretDown } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { Text, XStack, YStack } from 'tamagui';

import { useIsMobile } from '../../hooks/useIsMobile';
import { useMotion } from '../../hooks/useMotion';
import { PRICING_TIERS } from './constants';

export function Pricing() {
  const isMobile = useIsMobile();
  const { sectionReveal } = useMotion();

  return (
    <YStack
      id="pricing"
      paddingVertical={120}
      paddingHorizontal="$5"
      backgroundColor="$color2"
      borderTopWidth={1}
      borderBottomWidth={1}
      borderColor="$borderColor"
    >
      <YStack maxWidth={1100} alignSelf="center" width="100%">
        <motion.div
          {...sectionReveal}
          viewport={{ once: true }}
          style={{ marginBottom: 64 }}
        >
          <Text
            fontSize={isMobile ? 32 : 44}
            fontWeight="700"
            letterSpacing={-1.5}
            lineHeight={isMobile ? 38 : 52}
            color="$color12"
            textAlign="center"
          >
            Pricing
          </Text>
        </motion.div>

        <XStack
          gap="$4"
          justifyContent="center"
          flexDirection={isMobile ? 'column' : 'row'}
          alignItems={isMobile ? 'center' : 'stretch'}
        >
          {PRICING_TIERS.map((tier, index) => (
            <motion.div
              key={tier.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              style={{
                width: isMobile ? '100%' : undefined,
                maxWidth: isMobile ? 400 : undefined,
              }}
            >
              <YStack
                width={isMobile ? '100%' : 320}
                padding="$6"
                borderRadius="$4"
                borderWidth={1}
                borderColor={tier.highlighted ? '$accentBackground' : '$borderColor'}
                backgroundColor="$color3"
                gap="$4"
              >
                <Text fontSize={18} fontWeight="600" color="$color12">
                  {tier.name}
                </Text>

                <XStack alignItems="baseline" gap="$1">
                  <Text
                    fontSize={48}
                    fontWeight="700"
                    color="$color12"
                    letterSpacing={-2}
                    lineHeight={52}
                  >
                    {tier.price}
                  </Text>
                  <Text fontSize={16} color="$color8" fontWeight="400">
                    {tier.period}
                  </Text>
                </XStack>

                <Text fontSize={14} color="$color9" lineHeight={22}>
                  {tier.description}
                </Text>

                <YStack
                  height={1}
                  backgroundColor="$borderColor"
                  marginVertical="$2"
                />

                <YStack gap="$3">
                  {tier.features.map((feature) => (
                    <XStack key={feature} gap="$3" alignItems="center">
                      <Check size={16} color="var(--color8)" />
                      <Text fontSize={14} color="$color9" lineHeight={20}>
                        {feature}
                      </Text>
                    </XStack>
                  ))}
                </YStack>

                <XStack
                  marginTop="$4"
                  paddingVertical="$3"
                  borderRadius="$4"
                  alignItems="center"
                  justifyContent="center"
                  cursor="pointer"
                  backgroundColor={tier.highlighted ? '$accentBackground' : 'transparent'}
                  borderWidth={tier.highlighted ? 0 : 1}
                  borderColor="$color5"
                  hoverStyle={{ opacity: 0.8 }}
                  style={{ transition: 'opacity 0.2s ease' } as any}
                  tag="a"
                  onPress={() => {
                    const el = document.querySelector('#cta');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  <Text
                    fontSize={15}
                    fontWeight="600"
                    color={tier.highlighted ? '$accentColor' : '$color12'}
                  >
                    {tier.cta}
                  </Text>
                </XStack>
              </YStack>
            </motion.div>
          ))}
        </XStack>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.5 }}
          style={{ textAlign: 'center', marginTop: 32 }}
        >
          <Text fontSize={14} color="$color8" textAlign="center">
            All paid plans include a 14-day free trial. Annual plans save 17%.
          </Text>
        </motion.div>

        {/* FAQ */}
        <YStack gap="$1" marginTop="$8" maxWidth={640} alignSelf="center" width="100%">
          <Text fontSize="$6" fontWeight="600" color="$color12" marginBottom="$4" textAlign="center">
            Common questions
          </Text>
          {FAQ.map((faq) => (
            <FAQItem key={faq.q} question={faq.q} answer={faq.a} />
          ))}
        </YStack>
      </YStack>
    </YStack>
  );
}

const FAQ = [
  {
    q: 'What happens after the 3-app limit on Free?',
    a: 'You can keep using your 3 connected apps forever. To add more, upgrade to Pro — all your data and settings carry over instantly.',
  },
  {
    q: 'Can I switch plans or cancel anytime?',
    a: 'Yes. Upgrade, downgrade, or cancel from your dashboard. Changes take effect at the end of your billing cycle. No lock-in.',
  },
  {
    q: 'How does Torch handle permissions?',
    a: 'Torch respects the permissions of each connected app. If you can\'t access a file in Google Drive, you won\'t see it in Torch search results. No data leaks across permission boundaries.',
  },
  {
    q: 'What tools can I replace with Torch?',
    a: 'Torch includes Kanban (replaces Trello/Linear), Documents + Wiki (replaces Notion), OKRs (replaces Lattice), Calendar, Sprints, and Announcements. Most users replace 3-4 standalone tools.',
  },
];

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);

  return (
    <YStack
      borderBottomWidth={1}
      borderBottomColor="$borderColor"
      cursor="pointer"
      onPress={() => setOpen(!open)}
    >
      <XStack
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        paddingVertical="$4"
      >
        <Text fontSize="$4" fontWeight="500" color="$color11" flex={1}>
          {question}
        </Text>
        <div style={{
          display: 'inline-flex',
          transition: 'transform 0.2s cubic-bezier(0.25, 1, 0.5, 1)',
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          flexShrink: 0,
          marginLeft: 12,
        }}>
          <CaretDown size={16} color="var(--color8)" />
        </div>
      </XStack>
      {open && (
        <Text fontSize="$3" color="$color8" lineHeight={22} paddingBottom="$4">
          {answer}
        </Text>
      )}
    </YStack>
  );
}
